import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Alert, Button, Flex, Select, Stack, Text,TextInput } from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { IconExclamationCircle, IconInfoCircle } from "@tabler/icons-react";
import { z } from "zod";

import { AppLoader } from "@components/AppLoader";
import { CustomPhoneInput } from "@components/CustomPhoneInput";

import { setOnboardingAccessToken } from "@utils/api";

import { useStartPasswordlessOnboarding } from "@hooks/mutations/onboarding/useStartPasswordlessOnboarding";
import { useVerifyToken } from "@hooks/queries/onboarding/useVerifyToken";

import { OrganizationRegistration } from "./OrganizationRegistration";
import { ResumeOnboarding } from "./ResumeOnboarding";


const schema = z.object({
  email: z.string().email("Invalid email address").min(1, "Email is required"),
  title: z.string().min(1, "Title is required"),
  firstName: z.string().min(1, "First name is required").min(2, "Must be at least 2 characters"),
  lastName: z.string().min(1, "Last name is required").min(2, "Must be at least 2 characters"),
  phoneNumber: z.string().min(10, "Valid phone number is required"),
  gender: z.string().min(1, "Gender is required"),
  dob: z.string().optional(),
});

type Inputs = z.infer<typeof schema>;

interface Step1Props {
  token: string;
  onStarted: () => void;
}

export const Step1Registration = ({ token, onStarted }: Step1Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const { data: sessionData, isLoading: isVerifying, isError: isVerifyError } = useVerifyToken(token);
  const { mutate: startOnboarding, isLoading: isStarting } = useStartPasswordlessOnboarding();

  const form = useForm<Inputs>({
    mode: "uncontrolled",
    validate: zodResolver(schema),
    initialValues: {
      email: "",
      title: "",
      firstName: "",
      lastName: "",
      phoneNumber: "",
      gender: "",
      dob: "",
    },
  });

  const formatPhone = (val: string) => {
    const cleanVal = val.replace(/\s+/g, "");
    return cleanVal.startsWith("+") ? cleanVal : `+${cleanVal}`;
  };

  const handleFormSubmit = (values: Inputs) => {
    const payload = {
      token,
      email: values.email,
      title: values.title,
      first_name: values.firstName,
      last_name: values.lastName,
      phone_number: formatPhone(values.phoneNumber),
      gender: values.gender,
      ...(values.dob && { dob: values.dob }),
    };

    startOnboarding(payload, {
      onSuccess: (res: any) => {
        setOnboardingAccessToken(res.data.onboarding_access_token);
        onStarted();
      },
    });
  };

  if (isVerifying) return <AppLoader />;

  if (isVerifyError || !sessionData) {
    return (
      <Stack gap="md">
        <Alert icon={<IconExclamationCircle size={16} />} title={t("onboarding-invalid-link")} color="red">
          {t("onboarding-invalid-link-desc")}
        </Alert>
        <ResumeOnboarding onResumed={onStarted} />
      </Stack>
    );
  }

  if (["pending_review", "approved"].includes(sessionData.status)) {
    return (
      <Stack gap="md">
        <Alert icon={<IconInfoCircle size={16} />} title="Application Already Submitted" color="blue">
          {t("onboarding-already-submitted")}
        </Alert>
        {sessionData.status !== "approved" && <ResumeOnboarding onResumed={onStarted} />}
        <Button onClick={() => navigate("/")} fullWidth mt="md" size="md">
          Go to Sign In
        </Button>
      </Stack>
    );
  }

  if (sessionData.account_type === "organization") {
    return (
      <OrganizationRegistration
        token={token}
        identifier={sessionData.identifier}
      />
    );
  }

  return (
    <form onSubmit={form.onSubmit(handleFormSubmit)}>
      <Stack gap="md">
        <TextInput
          label={t("identifier")}
          value={sessionData.identifier}
          disabled
          description={t("identifier-desc")}
        />
        
        <TextInput
          withAsterisk
          label={t("email")}
          placeholder="e.g. johndoe@gmail.com"
          key={form.key("email")}
          {...form.getInputProps("email")}
        />
        
        <Flex direction={{ base: "column", sm: "row" }} gap="md" align={{ base: "stretch", sm: "flex-start" }} w="100%">
          <Select
            label={t("onboarding-honorific")}
            placeholder={t("onboarding-honorific-placeholder")}
            data={[
              { value: "Dr.", label: t("title-dr") },
              { value: "Prof.", label: t("title-prof") },
              { value: "Mr.", label: t("title-mr") },
              { value: "Mrs.", label: t("title-mrs") },
              { value: "Ms.", label: t("title-ms") },
              { value: "Miss", label: t("title-miss") }
            ]}
            withAsterisk
            key={form.key("title")}
            {...form.getInputProps("title")}
            style={{ flex: 1, width: "100%" }}
          />
          <TextInput
            withAsterisk
            label={t("first-name")}
            placeholder="e.g. John"
            key={form.key("firstName")}
            {...form.getInputProps("firstName")}
            style={{ flex: 2, width: "100%" }}
          />
        </Flex>
        
        <TextInput
          withAsterisk
          label={t("surname")}
          placeholder="e.g. Doe"
          key={form.key("lastName")}
          {...form.getInputProps("lastName")}
        />

        <Stack gap={4}>
          <Text fw={500} size="sm">{t("onboarding-phone")} <Text span c="red">*</Text></Text>
          <CustomPhoneInput
            phone={form.getValues().phoneNumber || ""}
            setPhone={(val) => form.setFieldValue("phoneNumber", val)}
            required
          />
          {form.errors.phoneNumber && <Text c="red" size="xs">{form.errors.phoneNumber as string}</Text>}
        </Stack>

        <Flex direction={{ base: "column", sm: "row" }} gap="md" align={{ base: "stretch", sm: "flex-start" }} w="100%">
          <Select
            label={t("onboarding-gender")}
            placeholder={t("onboarding-select-gender")}
            data={[
              { value: "male", label: t("gender-male") },
              { value: "female", label: t("gender-female") },
              { value: "other", label: t("gender-other") }
            ]}
            withAsterisk
            key={form.key("gender")}
            {...form.getInputProps("gender")}
            style={{ flex: 1, width: "100%" }}
          />
          <TextInput
            type="date"
            label={t("onboarding-dob")}
            key={form.key("dob")}
            {...form.getInputProps("dob")}
            style={{ flex: 1, width: "100%" }}
          />
        </Flex>

        <Button type="submit" loading={isStarting} fullWidth mt="xl" size="md">
          Continue Onboarding
        </Button>
      </Stack>
    </form>
  );
};
